import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore } from './store/authStore';
import { registerNavigate } from './utils/navigation';
import { refreshToken } from './api/client';
import { usePermissions } from './hooks/usePermission';
import AppLayout from './components/layout/AppLayout';
import { SetupWizardGuard } from './components/SetupWizardGuard';
import { useAuthStore as useAuthStoreForGuard } from './store/authStore';
import LoginPage from './pages/Login';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/Dashboard';
import ClientsPage from './pages/Clients/ClientsPage';
import ClientDetailPage from './pages/Clients/ClientDetailPage';
import CalendarPage from './pages/Calendar/CalendarPage';
import EventsPage from './pages/Events/EventsPage';
import EventDetailPage from './pages/Events/EventDetailPage';
import CollaboratorsPage from './pages/Collaborators/CollaboratorsPage';
import SettingsPage from './pages/Settings/SettingsPage';
import CatalogPage from './pages/Catalog/CatalogPage';
import SuppliersPage from './pages/Suppliers/index.js';
import FinancePage from './pages/Finance/FinancePage';
import ProjectsPage from './pages/Projects/ProjectsPage';
import ProjectDetailPage from './pages/Projects/ProjectDetailPage';
import ExternalPhotographersPage from './pages/ExternalPhotographers/ExternalPhotographersPage';
import PerformancesPage from './pages/Performances/PerformancesPage';
import UserManagementPage from './pages/Users/UserManagementPage';
import ProfilePage from './pages/Profile/ProfilePage';
import AuthGoogleSuccess from './pages/AuthGoogleSuccess';
import Auth2FAVerifyPage from './pages/Auth2FAVerifyPage';
import { TasksPage } from './pages/Tasks/TasksPage';
import { useIsMobile } from './hooks/useIsMobile';

// On page load the access token is gone (not persisted). If the user has a
// valid httpOnly refresh-token cookie we silently get a new access token before
// rendering anything, so protected routes don't flash a redirect to /login.
function useBootstrapAuth() {
  const { accessToken, user, twoFactorPending, logout } = useAuthStore();
  const [ready, setReady] = useState(false);
  // Guard against React StrictMode double-invocation.
  const bootstrapping = useRef(false);

  useEffect(() => {
    // 2FA pending — skip refresh, let RequireAuth handle redirect.
    if (twoFactorPending) { setReady(true); return; }

    // Already have a live token → nothing to do.
    if (accessToken) { setReady(true); return; }

    // No persisted user either → definitely not logged in.
    if (!user) { setReady(true); return; }

    // Use the shared singleton so concurrent apiFetch retries don't race.
    if (bootstrapping.current) return;
    bootstrapping.current = true;

    // User profile is in localStorage but token is gone → try silent refresh.
    refreshToken()
      .then(token => {
        if (!token) {
          // If refresh set twoFactorPending, don't clear state — RequireAuth will redirect
          if (!useAuthStore.getState().twoFactorPending) logout();
        }
      })
      .catch(() => logout())
      .finally(() => setReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  return ready;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore(s => s.accessToken);
  const twoFactorPending = useAuthStore(s => s.twoFactorPending);
  if (!token) return <Navigate to="/login" replace />;
  if (twoFactorPending) return <Navigate to="/auth/2fa-verify" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const user = useAuthStoreForGuard(s => s.user);
  if (user?.role !== 'ADMIN') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function RequirePermission({ permKey, children }: { permKey: string | any; children: React.ReactNode }) {
  const { hasPermission, isLoading } = usePermissions();
  const allowed = hasPermission(permKey);
  if (isLoading) return null;
  if (!allowed) return <Navigate to="/tasks" replace />;
  return <>{children}</>;
}

// Registers the React Router navigate fn in the singleton so apiFetch can use it.
function NavigationRegistrar() {
  const navigate = useNavigate();
  useEffect(() => { registerNavigate(navigate); }, [navigate]);
  return null;
}

function RequireGuest({ children }: { children: React.ReactNode }) {
  const token = useAuthStore(s => s.accessToken);
  if (token) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function MobileAwareIndex() {
  const isMobile = useIsMobile();
  return <Navigate to={isMobile ? '/tasks' : '/dashboard'} replace />;
}

export default function App() {
  const [theme] = useState<'dark' | 'light'>('dark');
  const ready = useBootstrapAuth();
  if (!ready) return null; // blank while silent-refreshing (< 200ms in practice)

  return (
    <BrowserRouter>
      <NavigationRegistrar />
      <Toaster
        theme={theme}
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            color: 'var(--color-text)',
          },
        }}
      />
      <Routes>
        <Route path="/login" element={<RequireGuest><LoginPage /></RequireGuest>} />
        <Route path="/forgot-password" element={<RequireGuest><ForgotPasswordPage /></RequireGuest>} />
        <Route path="/reset-password" element={<RequireGuest><ResetPasswordPage /></RequireGuest>} />
        <Route path="/auth/google/success" element={<AuthGoogleSuccess />} />
        <Route path="/auth/2fa-verify" element={<Auth2FAVerifyPage />} />
        <Route path="/" element={<RequireAuth><SetupWizardGuard><AppLayout /></SetupWizardGuard></RequireAuth>}>
          <Route index element={<MobileAwareIndex />} />
          <Route path="dashboard" element={<RequirePermission permKey="dashboard.view"><DashboardPage /></RequirePermission>} />
          <Route path="calendar" element={<RequirePermission permKey="calendar.view"><CalendarPage /></RequirePermission>} />
          <Route path="clients" element={<RequirePermission permKey="clients.view"><ClientsPage /></RequirePermission>} />
          <Route path="clients/:id" element={<RequirePermission permKey="clients.view"><ClientDetailPage /></RequirePermission>} />
          <Route path="events" element={<RequirePermission permKey="events.view"><EventsPage /></RequirePermission>} />
          <Route path="events/:id" element={<RequirePermission permKey="events.view"><EventDetailPage /></RequirePermission>} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="collaborators" element={<RequirePermission permKey="collaborators.view"><CollaboratorsPage /></RequirePermission>} />
          <Route path="settings" element={<RequirePermission permKey="settings.view"><SettingsPage /></RequirePermission>} />
          <Route path="catalog" element={<RequirePermission permKey="catalog.view"><CatalogPage /></RequirePermission>} />
          <Route path="suppliers" element={<RequirePermission permKey="suppliers.view"><SuppliersPage /></RequirePermission>} />
          <Route path="finance" element={<RequirePermission permKey="finance.view"><FinancePage /></RequirePermission>} />
          <Route path="projects" element={<RequirePermission permKey="projects.view"><ProjectsPage /></RequirePermission>} />
          <Route path="projects/:id" element={<RequirePermission permKey="projects.view"><ProjectDetailPage /></RequirePermission>} />
          <Route path="external-photographers" element={<RequirePermission permKey="external-photographers.view"><ExternalPhotographersPage /></RequirePermission>} />
          <Route path="performances" element={<PerformancesPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="users" element={<RequirePermission permKey="users.manage"><UserManagementPage /></RequirePermission>} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
